import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Activity, ArrowLeft, Clock, Database, ExternalLink, RefreshCw, Search,
  ShieldCheck, Target, TrendingUp, X,
} from 'lucide-react'
import { getBookLogo } from '../lib/logoMaps.js'
import { getNormalizedTeamDisplay } from '../lib/oddsNormalization.js'
import { getTeamLogoPath } from '../lib/teamLogos.js'
import {
  buildEventIntelligence,
  buildOfferDeepLink,
  fetchEventIntelligenceBundle,
  fetchOfferProvenance,
  proofPageForBook,
} from '../lib/eventIntelligence.js'
import './EventIntelligencePage.css'

const REFRESH_MS = 30_000
const STALE_AFTER_MS = 5 * 60_000

function formatOdds(value) {
  const odds = Number(value)
  if (!Number.isFinite(odds)) return '—'
  return odds > 0 ? `+${odds}` : String(odds)
}

function formatLine(value) {
  if (value == null || value === '') return '—'
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? `+${number}` : String(value)
}

function formatAge(ageMs) {
  if (!Number.isFinite(ageMs)) return 'unknown age'
  const minutes = Math.floor(ageMs / 60_000)
  if (minutes < 60) return `${minutes}m old`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}h ${minutes % 60}m old`
  return `${Math.floor(hours / 24)}d ${hours % 24}h old`
}

function sportKeyForLeague(league) {
  const value = String(league || '').toUpperCase()
  if (value === 'MLB') return 'baseball_mlb'
  if (value === 'NBA') return 'basketball_nba'
  if (value === 'WNBA') return 'basketball_wnba'
  if (value === 'NFL') return 'americanfootball_nfl'
  if (value === 'CFB' || value === 'NCAAF') return 'americanfootball_ncaaf'
  if (value === 'NHL') return 'icehockey_nhl'
  if (value === 'CBB' || value === 'NCAAB') return 'basketball_ncaab'
  return ''
}

function OpportunityStrip({ opportunities, navigateTo }) {
  const cards = [
    { id: 'positive-ev', label: '+EV', count: opportunities.evs.length, color: '#38dda8' },
    { id: 'arbitrage', label: 'Arbitrage', count: opportunities.arbs.length, color: '#f2bd55' },
    { id: 'middles', label: 'Middles', count: opportunities.middles.length, color: '#57b8ef' },
  ]
  return (
    <div className="eip-opportunity-strip">
      {cards.map((card) => (
        <button key={card.id} style={{ '--eip-accent': card.color }} onClick={() => navigateTo('liveodds', card.id)}>
          <span>{card.label}</span>
          <strong>{card.count}</strong>
        </button>
      ))}
    </div>
  )
}

function ProofDrawer({ proof, onClose }) {
  if (!proof?.row) return null
  const deepLink = buildOfferDeepLink(proof.row)
  return (
    <div className="eip-drawer-overlay" onClick={onClose}>
      <aside className="eip-drawer" onClick={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span>Offer proof · pid {proof.row.pid ?? 'unavailable'}</span>
            <h3>{proof.row.book} · {proof.row.bet_type} · {proof.row.selection} {formatLine(proof.row.line)}</h3>
          </div>
          <button onClick={onClose} aria-label="Close offer proof"><X size={18} /></button>
        </header>

        <div className="eip-drawer-actions">
          <a href={proofPageForBook(proof.row.book)} target="_blank" rel="noreferrer">Book proof page <ExternalLink size={12} /></a>
          {deepLink ? <a href={deepLink} target="_blank" rel="noreferrer">Open sportsbook offer <ExternalLink size={12} /></a> : <span>No constructible betslip link</span>}
        </div>

        {proof.loading ? <div className="eip-proof-state">Loading exact provenance index...</div> : null}
        {proof.error ? <div className="eip-proof-state is-error">{proof.error}</div> : null}
        {proof.provenance ? (
          <section className="eip-proof-block">
            <h4>Extractor provenance path</h4>
            <code>{proof.provenance.path || 'Path not encoded'}</code>
            <pre>{JSON.stringify(proof.provenance, null, 2)}</pre>
          </section>
        ) : null}
        {!proof.loading && !proof.provenance && !proof.error ? <div className="eip-proof-state">No provenance row was encoded for this pid.</div> : null}

        <section className="eip-proof-block">
          <h4>Exact normalized board row</h4>
          <pre>{JSON.stringify(proof.row, null, 2)}</pre>
        </section>
      </aside>
    </div>
  )
}

export default function EventIntelligencePage({ target, navigateTo, onBack }) {
  const [bundle, setBundle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [market, setMarket] = useState('ALL')
  const [search, setSearch] = useState('')
  const [proof, setProof] = useState(null)
  const targetKey = JSON.stringify(target || {})

  const refresh = useCallback(async () => {
    if (!target?.awayTeam || !target?.homeTeam) return
    try {
      setLoading(true)
      setBundle(await fetchEventIntelligenceBundle())
      setError(null)
    } catch (fetchError) {
      setError(fetchError.message || 'Event intelligence data unavailable')
    } finally {
      setLoading(false)
    }
  }, [targetKey])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, REFRESH_MS)
    return () => clearInterval(interval)
  }, [refresh])

  const intelligence = useMemo(
    () => bundle && target ? buildEventIntelligence(bundle, target) : null,
    [bundle, targetKey]
  )
  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase()
    return (intelligence?.groups || []).filter((group) => (
      (market === 'ALL' || group.market === market)
      && (!query || [group.market, group.period, group.selection, group.rawSelection, group.line]
        .join(' ').toLowerCase().includes(query))
    ))
  }, [intelligence, market, search])
  const movers = useMemo(
    () => (intelligence?.movementSeries || []).filter((entry) => entry.deltaProbabilityPoints != null).slice(0, 12),
    [intelligence]
  )

  const openProof = useCallback(async (row) => {
    setProof({ row, provenance: null, loading: true, error: null })
    try {
      const provenance = await fetchOfferProvenance(row.pid)
      setProof({ row, provenance, loading: false, error: null })
    } catch (proofError) {
      setProof({ row, provenance: null, loading: false, error: proofError.message || 'Provenance unavailable' })
    }
  }, [])

  if (!target?.awayTeam || !target?.homeTeam) {
    return (
      <div className="eip-shell eip-empty-shell">
        <h2>No event selected</h2>
        <p>Open an event from the schedule to join its odds, movement, edges, and proof.</p>
        <button onClick={onBack}><ArrowLeft size={14} /> Back to dashboard</button>
      </div>
    )
  }

  const league = intelligence?.target?.league || target.league
  const awayName = getNormalizedTeamDisplay(intelligence?.target?.awayCode || target.awayTeam, league)
  const homeName = getNormalizedTeamDisplay(intelligence?.target?.homeCode || target.homeTeam, league)
  const sportKey = sportKeyForLeague(league)
  const capturedAt = intelligence?.proof?.capturedAt || null
  const ageMs = capturedAt ? Math.max(0, Date.now() - Date.parse(capturedAt)) : null
  const isStale = ageMs == null || ageMs > STALE_AFTER_MS

  return (
    <div className="eip-shell">
      <button className="eip-back" onClick={onBack}><ArrowLeft size={14} /> Back</button>

      <section className="eip-hero">
        <div className="eip-team eip-team-away">
          {getTeamLogoPath(awayName, sportKey) ? <img src={getTeamLogoPath(awayName, sportKey)} alt="" onError={(event) => { event.currentTarget.style.display = 'none' }} /> : null}
          <div><span>Away</span><strong>{awayName}</strong></div>
        </div>
        <div className="eip-event-meta">
          <span className="eip-league">{league || 'SPORT'}</span>
          <b>@</b>
          <span><Clock size={12} /> {intelligence?.eventDate || target.date || 'Date pending'} · {intelligence?.eventTime || 'Time pending'}</span>
        </div>
        <div className="eip-team eip-team-home">
          <div><span>Home</span><strong>{homeName}</strong></div>
          {getTeamLogoPath(homeName, sportKey) ? <img src={getTeamLogoPath(homeName, sportKey)} alt="" onError={(event) => { event.currentTarget.style.display = 'none' }} /> : null}
        </div>
      </section>

      <section className={`eip-release ${isStale ? 'is-stale' : 'is-current'}`}>
        <span><Database size={13} /> {isStale ? 'STALE RELEASE' : 'CURRENT RELEASE'}</span>
        <span>{intelligence?.books.length || 0} books</span>
        <span>{intelligence?.eventRows.length || 0} offers</span>
        <span>{intelligence?.markets.length || 0} markets</span>
        <span>{intelligence?.lineCount || 0} market lines</span>
        <span>{capturedAt ? `Captured ${new Date(capturedAt).toLocaleString()} · ${formatAge(ageMs)}` : 'Capture unavailable'}</span>
        <button onClick={refresh} title="Refresh event intelligence"><RefreshCw size={13} className={loading ? 'eip-spin' : ''} /></button>
      </section>

      {intelligence ? <OpportunityStrip opportunities={intelligence.opportunities} navigateTo={navigateTo} /> : null}
      {error ? <div className="eip-error">{error}</div> : null}

      <section className="eip-panel">
        <header className="eip-panel-header">
          <div><Activity size={15} /><h2>All captured offers</h2><span>{visibleGroups.length} rows</span></div>
          <div className="eip-filters">
            <select value={market} onChange={(event) => setMarket(event.target.value)}>
              <option value="ALL">All markets</option>
              {(intelligence?.markets || []).map((entry) => <option key={entry} value={entry}>{entry}</option>)}
            </select>
            <label><Search size={13} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Market, side, line" /></label>
          </div>
        </header>

        {loading && !intelligence ? <div className="eip-empty">Loading validated event data...</div> : null}
        {!loading && intelligence && intelligence.eventRows.length === 0 ? (
          <div className="eip-empty">This event has no captured offers in the current board release. The schedule event remains valid; the odds release is either stale, not offering it, or missing coverage.</div>
        ) : null}
        {intelligence?.eventRows.length ? (
          <div className="eip-table-wrap">
            <table className="eip-offer-table">
              <thead><tr><th>Market / selection</th>{intelligence.books.map((book) => <th key={book}>{getBookLogo(book) ? <img src={getBookLogo(book)} alt="" onError={(event) => { event.currentTarget.style.display = 'none' }} /> : null}<span>{book}</span></th>)}</tr></thead>
              <tbody>
                {visibleGroups.map((group) => (
                  <tr key={group.key}>
                    <td><strong>{group.market}</strong><span>{group.period} · {group.selection}{group.line == null ? '' : ` ${formatLine(group.line)}`}</span></td>
                    {intelligence.books.map((book) => {
                      const row = group.offers[book]
                      if (!row) return <td key={book} className="is-empty">—</td>
                      const isBest = Number(row.american) === group.bestAmerican
                      return (
                        <td key={book}>
                          <button className={`eip-price ${isBest ? 'is-best' : ''}`} onClick={() => openProof(row)} title="Inspect exact raw/provenance row">
                            <strong>{formatOdds(row.american)}</strong><small>{row.state || 'state unknown'}</small>
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <div className="eip-lower-grid">
        <section className="eip-panel">
          <header className="eip-panel-header"><div><TrendingUp size={15} /><h2>Largest moves</h2><span>{intelligence?.movementSeries.length || 0} tapes</span></div><button className="eip-panel-link" onClick={() => navigateTo('liveodds', 'line-movement')}>Full movement</button></header>
          {movers.length ? (
            <div className="eip-movers">
              {movers.map((entry) => (
                <div key={entry.key}><span>{entry.bookmaker} · {entry.marketLabel} · {entry.selection} {formatLine(entry.line)}</span><b>{formatOdds(entry.openingOdds)} → {formatOdds(entry.currentOdds)}</b><strong className={(entry.deltaProbabilityPoints || 0) > 0 ? 'up' : 'down'}>{entry.deltaProbabilityPoints > 0 ? '+' : ''}{entry.deltaProbabilityPoints.toFixed(1)}pp</strong></div>
              ))}
            </div>
          ) : <div className="eip-empty">No independently recorded movement for this event.</div>}
        </section>

        <section className="eip-panel eip-audit-panel">
          <header className="eip-panel-header"><div><ShieldCheck size={15} /><h2>Audit contract</h2></div></header>
          <div className="eip-audit-list">
            <span><Target size={13} /> Event identity: {league} · {intelligence?.eventName || `${awayName} @ ${homeName}`} · {intelligence?.eventDate || target.date}</span>
            <span><Database size={13} /> Release: {intelligence?.proof?.releaseId || 'unavailable'}</span>
            <span><Activity size={13} /> Every populated price cell opens its normalized row and `pid` provenance.</span>
            <a href="https://abet-board-data.abetco.workers.dev/data/cat_game-lines.json" target="_blank" rel="noreferrer">Current game-line artifact <ExternalLink size={12} /></a>
            <a href="https://abet-board-data.abetco.workers.dev/data/prov_game-lines.json" target="_blank" rel="noreferrer">Provenance index <ExternalLink size={12} /></a>
          </div>
        </section>
      </div>

      <ProofDrawer proof={proof} onClose={() => setProof(null)} />
    </div>
  )
}
